// Le bandeau qui dit qu'une partie de l'écran n'a pas pu se charger.
//
// Une requête en échec rendait, presque partout, une liste vide : « Aucune
// règle pour le moment », « Aucun mouvement ce mois-ci », zéro chantier à
// planifier. Le 06/10/2026, 55 règles de rapprochement ont ainsi disparu de
// l'écran sans un mot. Une erreur se voit ; une liste vide se croit.
//
// Plutôt que de corriger quarante écrans un par un — et de laisser passer le
// quarante et unième —, ce bandeau écoute le cache de requêtes de toute
// l'application. Dès qu'une requête de l'écran affiché échoue, il le dit, en
// français, avec un bouton pour réessayer. Les écrans qui gèrent déjà leurs
// erreurs continuent de le faire ; celui-ci rattrape tous les autres.

import { messageErreur } from '@imp/shared/message-erreur';
import { type Query, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, RotateCw } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';

/** Ce que charge chaque requête, dit comme on le dirait à quelqu'un. */
const QUOI: Record<string, string> = {
  'adresses-client': 'les adresses du client',
  alertes: 'les alertes',
  'alertes-traitees': 'les alertes déjà traitées',
  article: 'la prestation',
  articles: 'les prestations',
  'assistant-context': 'le contexte de l’assistant',
  categories: 'les catégories',
  chantier: 'le chantier',
  'chantier-documents': 'les documents du chantier',
  'chantier-evenements': 'les interventions du chantier',
  'chantier-historique': 'les devis et factures du chantier',
  'chantier-photos': 'les photos du chantier',
  'chantier-pointages': 'les pointages du chantier',
  chantiers: 'les chantiers',
  'chantiers-pilotage': 'les chantiers du pilotage',
  'clients-sans-code': 'les clients sans compte comptable',
  contact: 'le client',
  'contact-adresse': 'l’adresse du client',
  contacts: 'les clients',
  'contexte-chantier': 'le contexte du chantier',
  'dashboard-a-facturer': 'les chantiers à facturer',
  'dashboard-chiffres': 'les chiffres du tableau de bord',
  devis: 'les devis',
  'devis-externes': 'les devis à relancer',
  'devis-pdf': 'le devis',
  employes: 'les salariés',
  'employes-admin': 'les salariés',
  'employes-heures': 'les heures des salariés',
  equipes: 'les équipes',
  evenements: 'l’agenda',
  'export-comptable-reglages': 'les réglages de l’export comptable',
  facture: 'la facture',
  'facture-pdf': 'la facture',
  factures: 'les factures',
  'factures-en-attente': 'les factures en attente',
  'factures-exportables': 'les factures à exporter',
  'factures-pilotage': 'les factures du pilotage',
  'fiche-chantier': 'la fiche de chantier',
  'gcal-sync-etat': 'la synchronisation Google Agenda',
  marges: 'le récap des marges',
  membres: 'les membres de l’équipe',
  'menu-entreprise': 'les réglages du menu',
  organisation: 'les informations de la société',
  'plan-comptable': 'le plan comptable',
  'pointages-range': 'les pointages',
  'prospection-communes': 'les communes de prospection',
  'prospection-passages': 'les passages de prospection',
  questionnaire: 'le questionnaire',
  'questionnaire-builder': 'le questionnaire',
  questionnaires: 'les questionnaires',
  'regle-pause': 'la règle de pause',
  reglements: 'les règlements',
  'reglements-periode': 'les règlements de la période',
  'releves-heures': 'les relevés d’heures',
  'sms-conversation': 'la conversation',
  'sms-conversations': 'les conversations',
  'sms-messages': 'les messages',
  'sms-settings': 'les réglages des SMS',
  'suivi-chantiers': 'le suivi des chantiers',
  'suivi-colonnes': 'les colonnes du suivi',
  'suivi-facturation': 'le suivi de la facturation',
  'suivi-faits': 'les étapes faites',
  taches: 'les tâches',
  'taches-assignables': 'les personnes à qui confier une tâche',
  'taches-chantiers': 'les chantiers des tâches',
  'taux-tva': 'les taux de TVA',
  taux_tva: 'les taux de TVA',
  'treso-ajustements-projection': 'les ajustements de la projection',
  'treso-details': 'le détail des postes',
  'treso-mouvements': 'le relevé bancaire',
  'treso-postes': 'la grille prévisionnelle',
  'treso-regles': 'les règles de rapprochement',
  'treso-regles-projection': 'les règles de la projection',
  'treso-solde-initial': 'le solde de départ',
  types_evenement: 'les types d’intervention',
  unites: 'les unités',
  utilisateurs: 'les utilisateurs',
  vehicule: 'le véhicule',
  'vehicule-interventions': 'l’entretien du véhicule',
  vehicules: 'les véhicules',
  'ventes-crm': 'les factures du CRM',
  'ventes-externes': 'les ventes',
};

function quoi(q: Query): string {
  const cle = q.queryKey[0];
  return (typeof cle === 'string' ? QUOI[cle] : undefined) ?? 'une partie des données';
}

function majuscule(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Les requêtes de l'écran affiché qui sont en échec.
 *
 * « Actives » seulement : une requête d'un écran qu'on a quitté n'a plus rien
 * à signaler. On ne relance le calcul que quand l'ensemble des échecs change,
 * pas à chaque mouvement du cache — il en compte plusieurs par seconde.
 */
function useEchecs(): Query[] {
  const cache = useQueryClient().getQueryCache();
  const [signature, setSignature] = useState('');

  useEffect(() => {
    const calculer = (): void => {
      const s = cache
        .findAll({ type: 'active' })
        .filter((q) => q.state.status === 'error')
        .map((q) => q.queryHash)
        .sort()
        .join('|');
      setSignature((avant) => (avant === s ? avant : s));
    };
    calculer();
    return cache.subscribe(calculer);
  }, [cache]);

  return useMemo(
    () =>
      signature === ''
        ? []
        : cache.findAll({ type: 'active' }).filter((q) => q.state.status === 'error'),
    [signature, cache],
  );
}

export function BandeauEchecs() {
  const queryClient = useQueryClient();
  const echecs = useEchecs();
  if (echecs.length === 0) return null;

  // Trois requêtes sur les clients ne font qu'une ligne : on dit QUOI manque,
  // pas combien de fois on a essayé de le lire.
  const lignes = new Map<string, string>();
  for (const q of echecs) {
    const k = quoi(q);
    if (!lignes.has(k)) lignes.set(k, messageErreur(q.state.error));
  }
  const enCours = echecs.some((q) => q.state.fetchStatus === 'fetching');

  return (
    <div
      role="alert"
      className="border-destructive/40 bg-destructive/10 mb-5 rounded-md border px-4 py-3 text-sm"
    >
      <p className="text-destructive flex items-center gap-2 font-semibold">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        Cette page n’a pas pu tout charger.
      </p>
      <p className="text-destructive/90 mt-1">
        Ce qui s’affiche dessous est peut-être incomplet : une liste vide n’y veut pas dire
        qu’il n’y a rien.
      </p>
      <ul className="mt-2 space-y-1">
        {[...lignes].map(([k, m]) => (
          <li key={k}>
            <strong>{majuscule(k)}</strong> — {m}
          </li>
        ))}
      </ul>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="mt-3"
        disabled={enCours}
        onClick={() => {
          for (const q of echecs) {
            void queryClient.refetchQueries({ queryKey: q.queryKey, exact: true });
          }
        }}
      >
        <RotateCw className={`mr-1.5 h-4 w-4 ${enCours ? 'animate-spin' : ''}`} />
        {enCours ? 'Nouvel essai…' : 'Réessayer'}
      </Button>
    </div>
  );
}
